import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import { isPaymentMethodUsable, paymentMethodReturnWarning } from '@/utils/paymentMethodStatus';

const isAchMethod = (method) => {
    const mt = (method.methodType || '').toUpperCase();
    return mt === 'ACH' || mt === 'BANK';
};

const isApplePayMethod = (method) => (method.methodType || '').toUpperCase() === 'APPLE_PAY';

const formatMethodLabel = (method) => {
    if (isAchMethod(method)) {
        const bankName = method.achBankName || method.bankName || method.bank_name || 'Bank';
        const last4 = method.achAccountNumberLast4 || method.account_number_last_4 || method.accountLast4 || method.last4 || '';
        return `${bankName} •••• ${last4 || '0000'}`;
    }
    const brand = method.cardBrand || method.brand || method.card_brand || 'Card';
    const last4 = method.cardLast4 || method.last_4 || method.last4 || '';
    if (isApplePayMethod(method)) {
        return `Apple Pay — ${brand} •••• ${last4}`;
    }
    return `${brand} ending in ${last4 || '••••'}`;
};

const methodIcon = (method) => {
    if (isAchMethod(method)) return { name: 'bank', library: 'material' };
    if (isApplePayMethod(method)) return { name: 'logo-apple', library: 'ionicon' };
    return { name: 'card-outline', library: 'ionicon' };
};

export default function PaymentMethodPicker({ methods = [], selectedPaymentMethodID, onSelect, onAdd, onManage, testID = 'payment-method-picker', appearance = null }) {
    const renderOption = (method) => {
        const label = formatMethodLabel(method);
        const icon = methodIcon(method);
        const paymentMethodID = method.paymentMethodID || method.id;
        const usable = isPaymentMethodUsable(method);
        const disabled = !onSelect || !usable;
        const isSelected = usable && selectedPaymentMethodID === paymentMethodID;
        return (
        <Pressable
            key={paymentMethodID}
            testID={`${testID}-${paymentMethodID}`}
            onPress={() => { if (!disabled) onSelect?.(paymentMethodID); }}
            disabled={disabled}
            accessibilityRole="radio"
            accessibilityLabel={method.achReturnedAt ? `${label}. ${paymentMethodReturnWarning}` : label}
            accessibilityState={{ selected: isSelected, disabled }}
            style={[
                styles.row,
                appearance?.inputColor && { backgroundColor: appearance.inputColor },
                isSelected && styles.selectedRow,
                isSelected && appearance?.baseContainerColor && { backgroundColor: appearance.baseContainerColor },
            ]}
        >
            <Unicon name={icon.name} library={icon.library} size={20} color={isSelected ? appearance?.baseColor : (appearance?.textColor ? `${appearance.textColor}B3` : null)} colorVariant={isSelected ? 'giving' : 'textSecondary'} />
            <View style={styles.methodDetails}>
                <Text style={[styles.label, appearance?.textColor && { color: appearance.textColor }, isSelected && styles.selectedLabel, isSelected && appearance?.baseColor && { color: appearance.baseColor }]}>{label}</Text>
                {method.achReturnedAt && <View style={styles.returnWarning} testID={`${testID}-return-${paymentMethodID}`}>
                    <Unicon name="warning" size={16} colorVariant="error" testID={`${testID}-return-icon-${paymentMethodID}`} />
                    <Text style={styles.returnWarningText}>{paymentMethodReturnWarning}</Text>
                </View>}
            </View>
            {isSelected && <Unicon name="checkmark" size={18} color={appearance?.baseColor} colorVariant="giving" />}
        </Pressable>
        );
    };

    return (
        <View style={styles.container} testID={testID}>
            <View style={styles.headerRow}>
                <Text style={[styles.title, appearance?.textColor && { color: appearance.textColor }]}>Payment method</Text>
                {methods.length > 0 && onManage && (
                    <Pressable
                        testID={`${testID}-manage`}
                        onPress={onManage}
                        accessibilityRole="button"
                        accessibilityLabel="Manage payment methods"
                        accessibilityState={{ disabled: false }}
                        style={styles.manageBtn}
                    >
                        <Unicon name="create-outline" size={16} color={appearance?.textColor ? `${appearance.textColor}B3` : null} colorVariant="textSecondary" />
                        <Text style={[styles.manageText, appearance?.textColor && { color: `${appearance.textColor}B3` }]}>Manage</Text>
                    </Pressable>
                )}
            </View>
            {methods.length === 0 ? (
                <Text style={[styles.empty, appearance?.textColor && { color: `${appearance.textColor}B3` }]} testID={`${testID}-no-methods`}>
                    No payment methods are saved. Add one before giving.
                </Text>
            ) : (
                methods.map(renderOption)
            )}
            <Pressable
                testID={`${testID}-add`}
                onPress={onAdd}
                disabled={!onAdd}
                accessibilityRole="button"
                accessibilityLabel="Add a payment method"
                accessibilityState={{ disabled: !onAdd }}
                style={styles.addRow}
            >
                <Unicon name="add-circle-outline" library="ionicon" size={20} color={appearance?.baseColor} colorVariant="giving" />
                <Text style={[styles.addLabel, appearance?.baseColor && { color: appearance.baseColor }]}>Add a payment method</Text>
            </Pressable>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
    container: {
        gap: spacing.sm,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    title: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    manageBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        padding: spacing.xs,
    },
    manageText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    empty: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        padding: spacing.sm,
        borderRadius: roundness.sm,
        backgroundColor: colors.surfaceVariant,
    },
    methodDetails: { flex: 1, gap: 4 },
    returnWarning: { flexDirection: 'row', gap: spacing.xs, alignItems: 'flex-start' },
    returnWarningText: { flex: 1, fontFamily: fonts.body, fontSize: typography.caption, color: colors.error },
    selectedRow: {
        backgroundColor: colors.givingContainer,
    },
    label: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.text,
    },
    selectedLabel: {
        color: colors.giving,
        fontFamily: fonts.bold,
    },
    addRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        paddingVertical: spacing.sm,
    },
    addLabel: {
        fontSize: typography.body,
        fontFamily: fonts.bold,
        color: colors.giving,
    },
}));
